<template>
  <div class="project-quick-links">
    <p class="top-title">
      {{ $t('tenantProjects.quickLinks') }}
    </p>
    <ul class="link-list">
      <template v-for="(item, index) in quickLinkList">
        <li
          v-if="item.showLink()"
          :key="index"
          class="link-item"
        >
          <a
            class="link-item__title"
            @click="item.onClick"
          >
            {{ item.titleText() }}
          </a>
          <span
            v-if="isEngineer && item.overviewText"
            class="link-item__overview"
          >
            {{ item.overviewText() }}
          </span>
        </li>
      </template>
    </ul>
    <div
      v-if="isTenant"
      class="regulations-links"
    >
      <a
        v-for="link in redirectLinksList"
        :key="link.key"
        :href="link.value"
        target="_blank"
        class="name-hyper-link"
      >
        {{ $t(`tenancy.${link.key}`) }}
      </a>
    </div>
  </div>
</template>

<script>
import { useStoreState } from '@/store'
import i18n from '@/utils/i18n'
import lodash from 'lodash'
import { computed, getCurrentInstance, reactive, toRefs, unref } from 'vue-demi'
import { mapActions, mapGetters } from 'vuex'

import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'

import { POST_FITOUT_STATUS } from '@/modules/tenantProjects/postFitOutLetter/consts'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { projectControlAchorIds, tenantProjectViewTabs } from '@/utils/consts'
import { tenancyType } from '@/utils/locale'

import { getUpdatorName } from '../contractors/utils'
import { isFitoutProject } from '../hook'
import { DEPOSIT_HISTORY_TYPE } from '../projectControl/deposit/consts'
import { useCurrentGreenFitout } from '../greenFitOut/hook'
import { buildGreenFitOutStatus } from '../greenFitOut/utils'

export default {
  inject: ['tabNames'],
  props: {
    openTnc: {
      type: Function,
      default: () => {},
    },
  },
  setup (props) {
    const { proxy } = getCurrentInstance()
    const isFitout = isFitoutProject()
    const state = useStoreState()
    const postFitouts = computed(() => lodash.get(state.value, 'postFitoutSubmission.postFitouts'))

    const navToSubTab = (submissionActiveKey) => {
      EventBus.emit('submission-sub-tab-change', submissionActiveKey)
      // Switch main Tab component
      EventBus.emit('common-tabs-container-select-tab', `#tab-${1}`, 1)
    }
    const currentGreenFitOut = useCurrentGreenFitout()
    const setupData = reactive({
      currentGreenFitOut,
      linkTitle: computed(() => {
        return unref(isFitout) ? i18n.t('tenantProjects.submissionFitoutComments') : i18n.t('tenantProjects.submissionComments')
      }),
      postFitoutMsg: computed(() => {
        let msg = i18n.t('emptyValue')
        if (isFitout) {
          const lastSubmission = lodash
            .orderBy(unref(postFitouts) || [], 'order', 'desc')[0]
          if (lastSubmission) {
            let num = unref(postFitouts).length > 1 ? unref(postFitouts).length : ''
            const status = lastSubmission.status === POST_FITOUT_STATUS.COMPLETED
              ? i18n.t('dashboard.completed')
              : i18n.t('dashboard.underReview')
            msg = `${i18n.t(
              'tenantProjects.tenantViewComments.postFitoutBatchNumber',
              [num],
            ).trim()}/${status}`
          }
          return msg
        }
      }),
      shouldShowGreenFitOutLink: computed(() => {
        return isFitout.value && setupData.greenFitOutMsg
      }),
      greenFitOutMsg: computed(() => {
        const { statusText, rawArray } = buildGreenFitOutStatus(currentGreenFitOut.value)
        if (rawArray.length === 0) {
          return null
        }
        return rawArray.length === 1 ? proxy.$t('emptyValue') : statusText
      }),
      quickLinkList: [
        {
          showLink: () => proxy.enableWorkPermitsFeature,
          onClick: () => setupData.navTo(tenantProjectViewTabs.contractorInfo),
          titleText: () => proxy.$t('tenantProjects.contractorInformation'),
          overviewText: () => proxy.contactorMsg,
        },
        {
          showLink: () => true,
          onClick: () => navToSubTab('1'),
          titleText: () => setupData.linkTitle,
          overviewText: () => proxy.submissionMsg,
        },
        {
          showLink: () => isFitout.value,
          onClick: () => navToSubTab('2'),
          titleText: () => proxy.$t('tenantProjects.postFitout.title'),
          overviewText: () => setupData.postFitoutMsg,
        },
        {
          showLink: () => setupData.shouldShowGreenFitOutLink,
          onClick: () => navToSubTab('3'),
          titleText: () => proxy.$t('tenantProjects.greenFitOut.title'),
          overviewText: () => setupData.greenFitOutMsg,
        },
        {
          showLink: () => proxy.canReadDeposit || proxy.isTenant,
          onClick: () => setupData.navTo(proxy.tabNames.length - 1, projectControlAchorIds.deposit),
          titleText: () => proxy.$t('tenantProjects.deposit'),
          overviewText: () => proxy.depositMsg,
        },
        {
          showLink: () => (proxy.canReadDeposit || proxy.isTenant) && proxy.management,
          onClick: () => setupData.navTo(proxy.tabNames.length - 1, projectControlAchorIds.managementFee),
          titleText: () => proxy.$t('tenantProjects.managementFee'),
          overviewText: () => proxy.managementMsg,
        },
        {
          showLink: () => (proxy.canReadInsurance || proxy.isTenant),
          onClick: () => setupData.navTo(proxy.tabNames.length - 1, projectControlAchorIds.insurance),
          titleText: () => proxy.$t('tenantProjects.insurance.title'),
          overviewText: () => proxy.insuranceMsg,
        },
        {
          showLink: () => proxy.enableWorkPermitsFeature,
          onClick: () => setupData.navTo(tenantProjectViewTabs.workPermit),
          titleText: () => proxy.$t('workPermit.title'),
          overviewText: () => proxy.workPermitMsg,
        },
        {
          showLink: () => (proxy.canReadInspection || proxy.isTenant),
          onClick: () => setupData.navTo(proxy.tabNames.length - 1, projectControlAchorIds.inspection),
          titleText: () => proxy.$t('tenantProjects.inspection.title'),
          overviewText: () => null,
        },
        {
          showLink: () => true,
          onClick: () => props.openTnc(),
          titleText: () => proxy.$t('tenantProjects.declaration'),
          overviewText: () => proxy.tncAcceptanceInfo,
        },
      ],
      navTo (tabIndex, anchorId) {
        EventBus.emit('common-tabs-container-select-tab', `#tab-${tabIndex}`, tabIndex, anchorId)
      },
    })
    return {
      ...toRefs(setupData),
      isFitout,
      navToSubTab,
    }
  },
  data () {
    return {
      tenantProjectViewTabs,
      projectControlAchorIds,
      insuranceMsg: '--',
      tenancyType,
    }
  },
  computed: {
    ...mapGetters('ccd', ['accounts']),
    ...mapGetters('settings', ['redirectLinksList']),
    ...mapGetters('tenantProject', ['currentTenantProject', 'currentTenantSubmission', 'deposit', 'management']),
    ...mapGetters('workPermit', ['workPermitsInProject']),
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    ...mapGetters('projectContractors', ['currentMainContractor', 'currentContractors', 'isMainUser']),
    ...mapGetters('regions', ['enableWorkPermitsFeature', 'regionName']),
    updatorName () {
      return getUpdatorName(this.currentMainContractor.updator || {}, this.currentMainContractor, this.accounts)
    },
    updatedTime () {
      const updatedAt = deep(this.currentMainContractor, 'updatedAt')
      return dateUtil.format(updatedAt, 'DD MMM YY HH:mm')
    },
    contactorMsg () {
      return this.currentMainContractor.company ? this.currentMainContractor.company.name : this.$t('emptyValue')
    },
    canReadDeposit () {
      return aclHelper.getAccess(tmsPermsTypes.project.readDeposit)
    },
    canReadInsurance () {
      return aclHelper.getAccess(tmsPermsTypes.project.readInsurance)
    },
    canReadInspection () {
      return aclHelper.getAccess(tmsPermsTypes.project.readInspection)
    },
    techComment () {
      return (this.currentTenantSubmission && this.currentTenantSubmission.techComment) || {}
    },
    designComment () {
      return (this.currentTenantSubmission && this.currentTenantSubmission.designComment) || {}
    },
    actionHistory () {
      return (this.currentTenantSubmission && this.currentTenantSubmission.actionHistory) || []
    },
    submissionMsg () {
      let result = this.$t('emptyValue')
      const submissions = lodash.get(this.currentTenantProject, 'submissions', [])
      const len = submissions.length
      if (submissions && len > 0) {
        const latestSubmission = lodash.orderBy(submissions, 'order', 'desc')[0]
        let status = latestSubmission.completedDate ? this.$t('dashboard.completed') : this.$t('dashboard.underReview')
        result = `${this.$t(
          'tenantProjects.tenantViewComments.batchNumber',
          [len],
        )}/${status}`
      }
      return result
    },
    workPermitMsg () {
      const mainWorkPermit = (this.workPermitsInProject.find(permit => permit.self.type === 'PROJECT') || {}).self
      if (mainWorkPermit && mainWorkPermit.status === 'WAPPR') {
        const submitHistory = mainWorkPermit.histories[mainWorkPermit.histories.length - 1]
        return this.$t('submittedByOn', [submitHistory.who.displayName, dateUtil.formatDate(submitHistory.createdAt, false)])
      } else if (mainWorkPermit && mainWorkPermit.status === 'APPR') {
        return this.$t('durationFromTo', [dateUtil.formatDate(mainWorkPermit.startDate, false), dateUtil.formatDate(mainWorkPermit.endDate, false)])
      } else {
        return ''
      }
    },
    tncAcceptanceInfo () {
      const tenantUserName = lodash.get(this.currentTenantProject, 'tncAcceptance.tenantUser.name', null)
      const mainContractorName = lodash.get(this.currentTenantProject, 'tncAcceptance.mainContractor.name', null)
      if (tenantUserName && mainContractorName) {
        return this.$t(`terms.${this.regionName}.tenantProjects.declarationAcceptedByAnd`, [tenantUserName, mainContractorName])
      } else if (tenantUserName || mainContractorName) {
        const name = tenantUserName || mainContractorName
        return this.$t(`terms.${this.regionName}.tenantProjects.declarationAcceptedBy`, [name])
      }
      return ''
    },
    depositMsg () {
      const result = this.getAmountInfo(this.deposit)
      return lodash.get(this.deposit, 'isWaived') ? this.$t('na') : result
    },
    managementMsg () {
      const result = this.getAmountInfo(this.management)
      return result
    },
  },
  mounted () {
    EventBus.$on('updateInsurance', (insurance) => {
      if (insurance.startDate && insurance.endDate) {
        this.insuranceMsg = this.$t('tenantProjects.insurance.validFrom', [
          this.$options.filters['formatDate'](insurance.startDate, 'DD MMM YYYY'),
          this.$options.filters['formatDate'](insurance.endDate, 'DD MMM YYYY'),
        ])
      }
    })
  },
  async created () {
    await this.loadRedirectLinks()
  },
  destroyed () {
    EventBus.$off('updateInsurance')
  },
  methods: {
    ...mapActions('settings', ['loadRedirectLinks']),
    getAmountInfo (amountObject) {
      let result = this.$t('emptyValue')
      const amount = lodash.get(amountObject, 'amount')
      const notifyAccountingHistory = lodash.get(amountObject, 'actionHistory', [])
        .find(history => history.actionType === DEPOSIT_HISTORY_TYPE.notifyAccounting)
      if (notifyAccountingHistory && amount !== null) {
        result = `${this.$globalProps.getCurrency()} ${amount}`
      }
      return result
    },
  },
}
</script>

<style lang="scss" scoped>
.project-quick-links {
  background-color: white;
  padding: 20px 15px 20px 16px;
  .top-title {
    font-size: 16px;
    font-weight: bold;
  }
  .link-list {
    list-style: none;
    margin-left: 0;
    width: 100%;
  }
  .link-item {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    border: solid 1px #ededed;
    padding: 10px 15px;
    &:not(:last-child) {
      margin-bottom: 10px;
    }

    &__title {
      display: inline-block;
      text-decoration: underline;
      color: #0c3a58;
      margin-right: 10px;
    }
    &__overview {
      font-style: italic;
      font-size: 12px;
      color: #6c6767;
      text-align: right;
    }
  }
  .regulations-links {
    margin-top: 30px;
    line-height: 2;
    font-weight: bold;
    > a {
      display: block;
    }
  }
}
</style>
